import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useDemoPopup } from "@/pages/LandingPage";

export const FAQ = ({ metierOverride }) => {
  const { openDemoPopup } = useDemoPopup();
  
  const baseFaqs = [
    {
      question: "Quels services sont inclus dans les packs ?",
      answer:
        "Chaque pack combine plusieurs de nos 12 solutions : IA vocale, rappels automatiques, WhatsApp, relance devis, CRM, prise de RDV, Google Ads, Facebook Ads, site web, SEO et reporting. Le pack Starter couvre l'essentiel (IA + rappels + WhatsApp), tandis que le pack Domination inclut l'ensemble de nos solutions pour une couverture totale.",
    },
    {
      question: "Est-ce que ça fonctionne avec mon organisation actuelle ?",
      answer:
        "Oui. Le système s'adapte à votre fonctionnement. Si vous avez déjà un CRM (Bexio, HubSpot, Pipedrive, Salesforce, Odoo…), nous pouvons y connecter toute l'infrastructure IA grâce à notre option Intégration CRM Externe. Aucun changement de vos outils existants n'est nécessaire.",
    },
    {
      question: "Comment fonctionnent les campagnes Google Ads et Facebook Ads ?",
      answer:
        "Google Ads cible les clients qui cherchent un artisan maintenant (recherche active) — idéal pour les métiers d'urgence comme plombier ou serrurier. Facebook Ads crée la demande avec des visuels et formulaires ciblés — idéal pour les métiers de projet comme cuisiniste ou paysagiste. Nous gérons la création, le ciblage et l'optimisation. Le budget publicitaire reste à votre charge et est payé directement aux plateformes.",
    },
    {
      question: "Est-ce que je garde le contrôle ?",
      answer:
        "Absolument. Vous gardez toujours le contrôle de vos interventions et décisions. Le système vous transmet les informations qualifiées via le CRM et les notifications WhatsApp. C'est vous qui validez les interventions et gérez votre planning.",
    },
    {
      question: "En combien de temps la solution est-elle installée ?",
      answer:
        "La mise en place est réalisée en moins de 7 jours pour les packs standards. Pour les intégrations CRM externes ou les configurations multi-lignes avancées, comptez 2 à 3 semaines. Nous commençons par un diagnostic de votre situation, puis on configure le système selon vos besoins.",
    },
    {
      question: "Le système peut-il gérer les appels en dehors des heures de bureau ?",
      answer:
        "Oui, le système est opérationnel 24h/24, 7j/7. Il prend en charge tous les appels, même la nuit et les week-ends. Vous pouvez choisir une couverture hors horaires uniquement ou 24/7 complète selon vos besoins.",
    },
    {
      question: "Puis-je combiner Google Ads et Facebook Ads ?",
      answer:
        "Oui, c'est même notre recommandation pour une couverture maximale. Le combo permet de capter la demande active (Google) et de créer de la demande latente (Facebook). Les packs Croissance et Domination incluent cette option avec des tarifs combinés avantageux.",
    },
    {
      question: "Y a-t-il un engagement minimum ?",
      answer:
        "Nous proposons un accompagnement flexible. Vous pouvez commencer avec un pack Starter pour tester l'impact sur votre activité. Les détails d'engagement sont définis ensemble lors du devis personnalisé selon vos besoins.",
    },
  ];

  // Add ville-specific FAQ at the top when on a ville page
  const villeFaqs = [];
  if (metierOverride?.ville) {
    const v = metierOverride.ville;
    const t = metierOverride.title?.toLowerCase();
    villeFaqs.push({
      question: `Pourquoi choisir Lead Artisan pour les ${t}s à ${v.nom} ?`,
      answer: `${v.description} Notre solution IA est spécialement calibrée pour le marché de ${v.nom} (${v.canton}). Nous connaissons les quartiers (${v.quartiers.slice(0, 3).join(", ")}...), la concurrence locale et les canaux publicitaires qui performent dans votre zone.`,
    });
    villeFaqs.push({
      question: `Comment fonctionne l'acquisition de clients pour un ${t} à ${v.nom} ?`,
      answer: `Nous combinons Google Ads (pour capter les recherches "${t} ${v.nom}") et Facebook Ads (pour cibler les propriétaires à ${v.nom} et ses environs). Notre IA optimise les campagnes en temps réel pour maximiser le nombre de leads qualifiés dans votre zone d'intervention.`,
    });
  }

  const faqs = [...villeFaqs, ...baseFaqs];

  return (
    <section id="faq" className="py-14 lg:py-28 blueprint-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12 lg:mb-16">
            <p className="section-label text-primary mb-4">FAQ</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 mb-4 tracking-tight">
              {metierOverride?.ville
                ? <>Questions fréquentes — <span className="text-primary">{metierOverride.title} à {metierOverride.ville.nom}</span></>
                : "Questions fréquentes"}
            </h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-xl mx-auto">
              Tout ce que vous devez savoir avant de démarrer.
            </p>
          </div>

          {/* Accordion */}
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={faq.question}
                value={`item-${index}`}
                data-testid={`faq-item-${index}`}
                className="bg-white border-2 border-slate-200 rounded-md px-5 sm:px-6 transition-all duration-300 hover:border-primary/40"
                style={{ boxShadow: '3px 3px 0px rgba(30, 90, 158, 0.06)' }}
              >
                <AccordionTrigger className="text-left font-bold text-slate-900 hover:text-primary py-5 text-sm sm:text-base" style={{ fontFamily: 'Barlow, sans-serif' }}>
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pb-5 text-sm sm:text-base">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* CTA Link */}
          <div className="mt-12 text-center">
            <p className="text-slate-500 mb-4">
              Vous ne trouvez pas la réponse à votre question ?
            </p>
            <button
              onClick={openDemoPopup}
              data-testid="faq-cta"
              className="text-accent font-bold hover:underline transition-all"
            >
              Demandez une démo pour en discuter
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
